export const IMAGE_COMPRESSION = {
  maxWidth: 800,
  maxHeight: 800,
  quality: 0.85,
  format: 'image/jpeg',
}

/**
 * Compresses an image file before upload to save bandwidth and storage.
 * Maintains aspect ratio while ensuring dimensions don't exceed maxWidth/maxHeight.
 */
export const compressImage = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      // Create an image object
      const img = new Image()

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (
          width > IMAGE_COMPRESSION.maxWidth ||
          height > IMAGE_COMPRESSION.maxHeight
        ) {
          if (width > height) {
            // Landscape image
            height = Math.round(height * (IMAGE_COMPRESSION.maxWidth / width))
            width = IMAGE_COMPRESSION.maxWidth
          } else {
            // Portrait image
            width = Math.round(width * (IMAGE_COMPRESSION.maxHeight / height))
            height = IMAGE_COMPRESSION.maxHeight
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        // Draw resized image to canvas
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(img.src)
          reject(new Error('Could not get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob with specified quality
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src)
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }
            resolve(blob)
          },
          IMAGE_COMPRESSION.format,
          IMAGE_COMPRESSION.quality,
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to load image for compression'))
      }

      // Load image from File
      img.src = URL.createObjectURL(file)
    } catch (error) {
      reject(error)
    }
  })
}
